import { Trans } from "@lingui/react";
import { WIcon } from "@mobility-item-page/client";
import { Alert } from "@warp-ds/elements/react/alert";
import { Button } from "@warp-ds/elements/react/button";
import { useValidDealerItemContext } from "../context/DealerItemProviderContext.tsx";
import { useModals } from "../context/ModalContext.tsx";
import { useTracking } from "../utils/pulseHelper.ts";

export const ReserveButton = () => {
  const {
    external: { isLoggedIn },
  } = useValidDealerItemContext();
  const { trackDigitalRetailing } = useTracking();
  const { openLoginModal, openReserveCarModal } = useModals();

  const handleReserveClick = () => {
    // Track the click event for Digital Retailing button
    trackDigitalRetailing();
    if (!isLoggedIn) {
      openLoginModal();
      return;
    }
    openReserveCarModal();
  };

  return (
    <Button
      data-testid="reserve-button"
      variant="primary"
      fullWidth
      onClick={handleReserveClick}
      className="whitespace-nowrap md:min-w-[170px]"
    >
      <Trans id="dealer.reserveCar.reserve" message="Reserve car" />
    </Button>
  );
};
export const ReserveCar = () => {
  const {
    external: { reservationEnabled, isItemReserved, brand },
  } = useValidDealerItemContext();
  const { isReserved, openReserveCarInfoModal } = useModals();

  if (!reservationEnabled) return null;

  const handleShowMoreInfo = () => {
    openReserveCarInfoModal();
  };

  // TODO: Replace with actual logic to check if current user made the reservation
  const reservedByUser = true;

  return (
    <>
      {isReserved || isItemReserved ? (
        <Alert className="w-full" variant="info" show>
          <div data-testid="reserve-car-status" className="flex items-center gap-4">
            <div>
              {reservedByUser ? (
                // Car is reserved by the current user
                <>
                  <p className="text-m font-bold text-green-900">
                    <Trans id="dealer.reserveCar.alert.user.title" message="The car is reserved for you." />
                  </p>
                  <p className="text-sm">
                    <Trans
                      id="dealer.reserveCar.alert.user.description"
                      message="The dealer will contact you by phone to help you complete your car purchase."
                    />
                  </p>
                </>
              ) : (
                // Car is reserved by someone else (via Blocket)
                <>
                  <p className="text-m font-bold text-green-900">
                    <Trans
                      id="dealer.reserveCar.alert.title"
                      message="The car is reserved via {brand}"
                      values={{ brand: brand !== "DBA" ? brand.charAt(0) + brand.slice(1).toLowerCase() : brand }}
                    />
                  </p>
                  <p className="text-sm">
                    <Trans
                      id="dealer.reserveCar.alert.description"
                      message="You can of course contact the dealer if you are interested in the car."
                    />
                  </p>
                </>
              )}
            </div>
          </div>
        </Alert>
      ) : (
        <div data-testid="reserve-car" className="border-2 rounded-8 p-16">
          <h2 className="text-ml mb-16">
            <Trans id="dealer.reserveCar.title" message="Reserve this car!" />
          </h2>

          <ul className="flex flex-col gap-2 mb-12">
            <li className="flex gap-4">
              <WIcon name="Check" className="s-icon-positive shrink-0 mt-2" />
              <p className="text-body mb-0">
                <Trans id="dealer.reserveCar.noWorries" message="Don't worry about the car being sold" />
              </p>
            </li>
            <li className="flex gap-4">
              <WIcon name="Check" className="s-icon-positive shrink-0 mt-2" />
              <p className="text-body mb-0">
                <Trans id="dealer.reserveCar.reserveFree" message="Reserve the car for free" />
              </p>
            </li>
            <li className="flex gap-4">
              <WIcon name="Check" className="s-icon-positive shrink-0 mt-2" />
              <p className="text-body mb-0">
                <Trans id="dealer.reserveCar.dealerContact" message="The dealer will contact you shortly" />
              </p>
            </li>
          </ul>

          <div className="mb-12">
            <Button onClick={handleShowMoreInfo} variant="link">
              <Trans id="dealer.reserveCar.showMoreInfo" message="Show more information" />
            </Button>
          </div>
          <ReserveButton />
        </div>
      )}
    </>
  );
};
